import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Megaphone, FloppyDisk, Trash, Plus, Image as ImageIcon,
  Link as LinkIcon, VideoCamera, Eye, X, ArrowSquareOut,
} from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import MuralPopup from "@/components/MuralPopup";

const inp =
  "w-full bg-[var(--bg-base)] border border-[var(--border)] rounded-sm px-3 py-2 text-sm focus:border-[var(--accent)] outline-none";

/**
 * Editor de mural.
 * Props:
 *   scope: "mine" | "global"
 *   title: label do topo
 */
export default function MuralEditor({ scope = "mine", pageTitle }) {
  const endpoint = scope === "global" ? "/mural/global" : "/mural/mine";

  const [form, setForm] = useState({
    title: "",
    body: "",
    links: [],
    images: [],
    videos: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [hasMural, setHasMural] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      if (data.mural) {
        setForm({
          title: data.mural.title || "",
          body: data.mural.body || "",
          links: data.mural.links || [],
          images: data.mural.images || [],
          videos: data.mural.videos || [],
        });
        setHasMural(true);
      } else {
        setHasMural(false);
      }
    } catch (e) {
      toast.error(errMsg(e, "Erro ao carregar mural"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(endpoint, {
        title: form.title,
        body: form.body,
        links: form.links.filter((l) => l.url?.trim()),
        images: form.images.filter((i) => i.url?.trim()),
        videos: form.videos.filter((v) => v.url?.trim()),
      });
      toast.success("Mural salvo. Aparecerá na próxima vez que os usuários logarem.");
      setHasMural(true);
      // Limpa o dismissed do próprio admin/cliente pra ver
      sessionStorage.removeItem("_mural_dismissed");
    } catch (e) { toast.error(errMsg(e, "Erro ao salvar")); }
    finally { setSaving(false); }
  };

  const del = async () => {
    setConfirmDel(false);
    try {
      await api.delete(endpoint);
      setForm({ title: "", body: "", links: [], images: [], videos: [] });
      setHasMural(false);
      toast.success("Mural apagado. Não aparecerá mais.");
    } catch (e) { toast.error(errMsg(e, "Erro ao apagar")); }
  };

  const upd = (key, i, k, v) => {
    setForm((f) => ({ ...f, [key]: f[key].map((it, x) => (x === i ? { ...it, [k]: v } : it)) }));
  };
  const rm = (key, i) => setForm((f) => ({ ...f, [key]: f[key].filter((_, x) => x !== i) }));

  const addLink = () => setForm((f) => ({ ...f, links: [...f.links, { label: "", url: "" }] }));
  const addImage = () => setForm((f) => ({ ...f, images: [...f.images, { url: "", alt: "" }] }));
  const addVideo = () => setForm((f) => ({ ...f, videos: [...f.videos, { name: "", url: "" }] }));

  const audienceForPreview = scope === "global" ? "client" : "member";

  return (
    <div className="fadeUp max-w-3xl" data-testid={`mural-editor-${scope}`}>
      <div className="label-caps mb-2 flex items-center gap-1">
        <Megaphone size={12} />
        {scope === "global" ? "Comunicado global" : "Mural do produtor"}
      </div>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-2">
        {pageTitle || (scope === "global" ? "Aviso para todos" : "Meu mural")}
      </h1>
      <p className="text-white/60 text-sm mb-8 max-w-2xl">
        {scope === "global"
          ? "Este aviso aparece em popup para TODOS os clientes e membros da plataforma logo após o login. Use para comunicados oficiais, manutenção, avisos legais, etc."
          : "Aparece em popup para os seus membros (alunos) logo após o login na área de conteúdo. Use para grupos de WhatsApp, avisos importantes, links de aplicativos e imagens."}
      </p>

      {loading ? (
        <div className="card-flat p-8 text-center text-white/40">Carregando...</div>
      ) : (
        <div className="space-y-4">
          {/* Título + Body */}
          <div className="card-flat p-5">
            <label className="label-caps block mb-2">Título (opcional)</label>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Ex: Bem-vindos alunos ou COMUNICADO IMPORTANTE"
              data-testid="mural-title-input"
              className={inp}
            />
            <label className="label-caps block mb-2 mt-4">Mensagem</label>
            <textarea
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              rows={5}
              placeholder="Escreva a mensagem que aparecerá no mural. Pode usar quebras de linha e MAIÚSCULAS."
              data-testid="mural-body-input"
              className={inp + " min-h-[120px] resize-y"}
            />
            <div className="text-[10px] text-white/40 mt-1">
              Aceita quebras de linha. Formato livre — MAIÚSCULAS ou minúsculas conforme sua preferência.
            </div>
          </div>

          {/* Imagens */}
          <div className="card-flat p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="label-caps flex items-center gap-1"><ImageIcon size={12} /> Imagens (opcional)</div>
              <button onClick={addImage} data-testid="mural-add-image" className="btn-ghost text-xs flex items-center gap-1"><Plus size={12} /> Adicionar</button>
            </div>
            {form.images.length === 0 && <div className="text-xs text-white/40">Nenhuma imagem.</div>}
            {form.images.map((img, i) => (
              <div key={`img-${i}`} className="grid md:grid-cols-[1fr_1fr_auto] gap-2 mb-2">
                <input placeholder="URL da imagem (https://...)" value={img.url} onChange={(e) => upd("images", i, "url", e.target.value)} data-testid={`mural-img-url-${i}`} className={inp} />
                <input placeholder="Descrição (alt)" value={img.alt || ""} onChange={(e) => upd("images", i, "alt", e.target.value)} data-testid={`mural-img-alt-${i}`} className={inp} />
                <button onClick={() => rm("images", i)} data-testid={`mural-rm-img-${i}`} className="btn-ghost px-2 text-[var(--danger)]"><Trash size={14} /></button>
              </div>
            ))}
          </div>

          {/* Vídeos */}
          <div className="card-flat p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="label-caps flex items-center gap-1"><VideoCamera size={12} /> Vídeos abaixo do mural (opcional)</div>
              <button onClick={addVideo} data-testid="mural-add-video" className="btn-ghost text-xs flex items-center gap-1"><Plus size={12} /> Adicionar</button>
            </div>
            {form.videos.length === 0 && <div className="text-xs text-white/40">Nenhum vídeo.</div>}
            {form.videos.map((v, i) => (
              <div key={`vid-${i}`} className="grid md:grid-cols-[1fr_2fr_auto] gap-2 mb-2">
                <input placeholder="Nome do vídeo" value={v.name || ""} onChange={(e) => upd("videos", i, "name", e.target.value)} data-testid={`mural-vid-name-${i}`} className={inp} />
                <input placeholder="URL (YouTube ou MP4 direto)" value={v.url} onChange={(e) => upd("videos", i, "url", e.target.value)} data-testid={`mural-vid-url-${i}`} className={inp} />
                <button onClick={() => rm("videos", i)} data-testid={`mural-rm-vid-${i}`} className="btn-ghost px-2 text-[var(--danger)]"><Trash size={14} /></button>
              </div>
            ))}
          </div>

          {/* Links / botões (aplicativos, sites) */}
          <div className="card-flat p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="label-caps flex items-center gap-1"><LinkIcon size={12} /> Links / botões (WhatsApp, Instagram, apps, sites)</div>
              <button onClick={addLink} data-testid="mural-add-link" className="btn-ghost text-xs flex items-center gap-1"><Plus size={12} /> Adicionar</button>
            </div>
            {form.links.length === 0 && <div className="text-xs text-white/40">Nenhum link.</div>}
            {form.links.map((l, i) => (
              <div key={`ln-${i}`} className="grid md:grid-cols-[1fr_2fr_auto] gap-2 mb-2">
                <input placeholder="Texto (ex: Grupo WhatsApp)" value={l.label} onChange={(e) => upd("links", i, "label", e.target.value)} data-testid={`mural-ln-label-${i}`} className={inp} />
                <input placeholder="URL (https://chat.whatsapp.com/... ou app://)" value={l.url} onChange={(e) => upd("links", i, "url", e.target.value)} data-testid={`mural-ln-url-${i}`} className={inp} />
                <button onClick={() => rm("links", i)} data-testid={`mural-rm-ln-${i}`} className="btn-ghost px-2 text-[var(--danger)]"><Trash size={14} /></button>
              </div>
            ))}
          </div>

          {/* Ações */}
          <div className="flex flex-wrap gap-2 items-center">
            <button
              onClick={save}
              disabled={saving}
              data-testid="mural-save"
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              <FloppyDisk size={14} weight="fill" /> {saving ? "Salvando..." : (hasMural ? "Salvar alterações" : "Publicar mural")}
            </button>
            <button
              onClick={() => setPreview(true)}
              data-testid="mural-preview"
              className="btn-ghost text-sm flex items-center gap-1"
            >
              <Eye size={13} /> Ver como aparece
            </button>
            {hasMural && (
              <button
                onClick={() => setConfirmDel(true)}
                data-testid="mural-delete"
                className="btn-ghost text-sm flex items-center gap-1 text-[var(--danger)] ml-auto"
              >
                <Trash size={13} /> Apagar mural
              </button>
            )}
          </div>
        </div>
      )}

      {/* Preview usa o próprio popup (mesma UX que o usuário verá) */}
      {preview && (
        <div className="fixed inset-0 z-[130] pointer-events-none">
          <div className="pointer-events-auto absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="pointer-events-auto max-w-md w-full">
              <MuralPreview
                data={{
                  ...form,
                  scope: scope === "global" ? "global" : "owner",
                  updated_at: "preview",
                  id: "preview",
                }}
                onClose={() => setPreview(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete */}
      {confirmDel && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setConfirmDel(false)}>
          <div className="bg-[var(--bg-base)] border border-[var(--border)] rounded-lg p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="font-display font-bold text-lg mb-2 flex items-center gap-2 text-[var(--danger)]"><Trash size={18} weight="fill" /> Apagar mural?</div>
            <p className="text-sm text-white/70 mb-4">Ele deixa de aparecer para todos que tiverem acesso. Você pode criar de novo depois.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmDel(false)} className="text-xs px-3 py-2 rounded-sm border border-white/10 hover:bg-white/5">Cancelar</button>
              <button onClick={del} data-testid="mural-confirm-del" className="text-xs px-3 py-2 rounded-sm bg-[var(--danger)] text-white font-bold flex items-center gap-1"><Trash size={12} weight="bold" /> Apagar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Preview inline (reusa o mesmo layout do popup)
function MuralPreview({ data, onClose }) {
  const isGlobal = data.scope === "global";
  const YT = /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/;
  return (
    <div className="bg-[var(--bg-base)] border rounded-lg shadow-2xl w-full max-h-[85vh] overflow-y-auto"
      style={{ borderColor: isGlobal ? "var(--accent)" : "var(--border)" }}>
      <div className="sticky top-0 flex items-center justify-between gap-2 px-4 py-2.5 border-b border-white/5"
        style={{ background: isGlobal ? "var(--accent)" : "var(--bg-surface)", color: isGlobal ? "#000" : "inherit" }}>
        <span className="text-[10px] uppercase tracking-widest font-bold flex items-center gap-1">
          <Megaphone size={13} weight="fill" /> Preview {isGlobal ? "· global" : "· do produtor"}
        </span>
        <button onClick={onClose} className="p-1 rounded-sm hover:bg-black/10"><X size={14} weight="bold" /></button>
      </div>
      <div className="p-4 space-y-3">
        {data.title && <div className="font-display font-black text-lg leading-tight">{data.title}</div>}
        {data.body && <div className="text-sm text-white/80 whitespace-pre-wrap">{data.body}</div>}
        {data.images?.length > 0 && (
          <div className="grid grid-cols-2 gap-2">
            {data.images.filter((i) => i.url).map((img, i) => (
              <img key={i} src={img.url} alt={img.alt || ""} className="w-full h-auto rounded-sm border border-white/10 object-cover max-h-40" onError={(e) => (e.currentTarget.style.display = "none")} />
            ))}
          </div>
        )}
        {data.videos?.length > 0 && (
          <div className="space-y-2">
            {data.videos.filter((v) => v.url).map((v, i) => {
              const m = v.url.match(YT);
              const embed = m ? `https://www.youtube-nocookie.com/embed/${m[1]}?rel=0` : null;
              return (
                <div key={i} className="rounded-sm overflow-hidden border border-white/10 bg-black">
                  {v.name && <div className="px-2 py-1 text-[10px] text-white/60 bg-white/5">{v.name}</div>}
                  {embed
                    ? <iframe src={embed} title={v.name} className="w-full" style={{ aspectRatio: "16/9" }} allowFullScreen />
                    : <video src={v.url} controls className="w-full" style={{ aspectRatio: "16/9" }} />}
                </div>
              );
            })}
          </div>
        )}
        {data.links?.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {data.links.filter((l) => l.url).map((l, i) => (
              <a key={i} href={l.url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[var(--accent)] text-black text-xs font-bold">
                <ArrowSquareOut size={11} weight="bold" /> {l.label || "Abrir"}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
